/**
 * Deep diff, used by the *mock* API to answer `GET /blueprints/{id}/diff`.
 *
 * The real diff is computed server-side (W2). The mock computes it the same way —
 * from two Blueprint documents — so the diff screen is exercised against paths that
 * actually exist in the document, in the same dialect
 * (`campaigns[0].ad_groups[2].keywords[5].text`) the console's tree builder parses.
 */
import type { BlueprintDiff, DiffEntry } from '@/api/types'

export interface RawDiff {
  added_paths: string[]
  removed_paths: string[]
  changed: DiffEntry[]
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function join(parent: string, child: string): string {
  return parent === '' ? child : `${parent}.${child}`
}

function index(parent: string, i: number): string {
  return `${parent}[${i}]`
}

function walk(before: unknown, after: unknown, path: string, out: RawDiff): void {
  if (Array.isArray(before) || Array.isArray(after)) {
    const beforeArr = Array.isArray(before) ? before : []
    const afterArr = Array.isArray(after) ? after : []
    const max = Math.max(beforeArr.length, afterArr.length)
    for (let i = 0; i < max; i += 1) {
      const childPath = index(path, i)
      const hasBefore = i < beforeArr.length
      const hasAfter = i < afterArr.length
      if (hasBefore && !hasAfter) out.removed_paths.push(childPath)
      else if (!hasBefore && hasAfter) out.added_paths.push(childPath)
      else walk(beforeArr[i], afterArr[i], childPath, out)
    }
    return
  }

  if (isPlainObject(before) && isPlainObject(after)) {
    const keys = new Set([...Object.keys(before), ...Object.keys(after)])
    for (const key of keys) {
      const childPath = join(path, key)
      const hasBefore = Object.prototype.hasOwnProperty.call(before, key)
      const hasAfter = Object.prototype.hasOwnProperty.call(after, key)
      if (hasBefore && !hasAfter) out.removed_paths.push(childPath)
      else if (!hasBefore && hasAfter) out.added_paths.push(childPath)
      else walk(before[key], after[key], childPath, out)
    }
    return
  }

  if (JSON.stringify(before) !== JSON.stringify(after)) {
    out.changed.push({ path, before: before ?? null, after: after ?? null })
  }
}

export function diffDocuments(before: unknown, after: unknown): RawDiff {
  const out: RawDiff = { added_paths: [], removed_paths: [], changed: [] }
  walk(before, after, '', out)
  return out
}

export function blueprintDiff(
  before: { version: number; document: unknown },
  after: { version: number; document: unknown },
): BlueprintDiff {
  const raw = diffDocuments(before.document, after.document)
  return {
    from_version: before.version,
    to_version: after.version,
    added_paths: raw.added_paths,
    removed_paths: raw.removed_paths,
    changed: raw.changed,
    summary: summarize(raw),
  }
}

export function summarize(raw: RawDiff): string {
  const parts: string[] = []
  if (raw.changed.length > 0) parts.push(`${raw.changed.length} changed`)
  if (raw.added_paths.length > 0) parts.push(`${raw.added_paths.length} added`)
  if (raw.removed_paths.length > 0) parts.push(`${raw.removed_paths.length} removed`)
  if (parts.length === 0) return 'No changes between these versions.'
  return `${parts.join(', ')} between the two versions.`
}
